import { useEffect, useState } from "preact/hooks";
import type { FlowState } from "../flow/machine";
import type { InstallRunner } from "../flow/runner";
import { fmtMs, hopRows, isHopTestTerminal } from "../groups/hop-test";
import { t } from "../i18n";
import { ForwardingTable } from "./GroupWidgets";

/**
 * Step 5c: read-backs for the affected members, then the live hop test — mandatory when any
 * of them reads back `source:"unverified"`, optional otherwise.
 */
export function HopTestStep({ runner, s }: { runner: InstallRunner; s: FlowState }) {
  const h = s.hopTest;
  const members = s.member?.members ?? [];
  const affected = s.member?.affected ?? [];
  const [caller, setCaller] = useState("");
  const [noReply, setNoReply] = useState(false);
  const [busy, setBusy] = useState(false);
  const jobId = h?.jobId;
  const terminal = h?.result ? isHopTestTerminal(h.result) : false;
  useEffect(() => {
    if (jobId && !terminal && !runner.store.state.error) void runner.pollHopTest();
  }, [runner, jobId, terminal]);

  const start = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    await runner.startHopTest(caller, noReply);
    setBusy(false);
  };
  const finish = async () => {
    setBusy(true);
    await runner.completeInstall();
    setBusy(false);
  };
  const skip = async () => {
    if (!runner.skipHopTest()) return;
    await finish();
  };
  const rows = h?.result ? hopRows(h.result, members) : [];

  return (
    <div class="panel">
      <h2 tabIndex={-1}>{t("hoptest.heading")}</h2>
      <section aria-labelledby="readback-h">
        <h3 id="readback-h">{t("hoptest.readback_heading")}</h3>
        <ForwardingTable members={members} positions={affected} />
      </section>
      {h?.required ? <p class="notice warn" role="status">{t("hoptest.required")}</p> : <p class="notice" role="status">{t("hoptest.optional")}</p>}
      {!jobId && (
        <form onSubmit={(e) => void start(e)} class="stack">
          <p>{t("hoptest.instructions", { main: s.group?.mainNumber ?? "" })}</p>
          <div>
            <label for="caller">{t("hoptest.caller_label")}</label>
            <input id="caller" class="ltr" type="tel" placeholder="+9715…" required value={caller} onInput={(e) => setCaller((e.target as HTMLInputElement).value)} />
          </div>
          <label style="font-weight:400">
            <input type="checkbox" checked={noReply} onChange={(e) => setNoReply((e.target as HTMLInputElement).checked)} /> {t("hoptest.no_reply_label")}
          </label>
          <div class="row">
            <button type="submit" class="primary" disabled={busy || !caller.trim()}>{t("hoptest.start")}</button>
            {!h?.required && <button type="button" disabled={busy} onClick={() => void skip()}>{t("hoptest.skip")}</button>}
          </div>
        </form>
      )}
      {jobId && (
        <section aria-live="polite">
          <p><strong>{terminal ? (h?.passed ? t("hoptest.passed") : t("hoptest.failed")) : t("hoptest.running")}</strong></p>
          <p class="hint">{t("hoptest.call_now", { main: s.group?.mainNumber ?? "", caller: h?.callerNumber ?? "" })}</p>
          <div class="scroll-x">
            <table>
              <thead>
                <tr><th>{t("hoptest.col.hop")}</th><th>{t("hoptest.col.expected")}</th><th>{t("hoptest.col.rang")}</th><th>{t("hoptest.col.ttr")}</th><th>{t("hoptest.col.forwarded_from")}</th><th>{t("hoptest.col.result")}</th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.hop} class={r.outcome === "ok" ? "" : r.outcome === "pending" ? "" : "mismatch"}>
                    <td>{r.hop}</td>
                    <td class="ltr">{t("hoptest.member", { position: r.expectedPosition, number: r.expectedNumber ?? "" })}</td>
                    <td class="ltr">{r.rangPosition === null ? "—" : t("hoptest.member", { position: r.rangPosition, number: r.rangNumber ?? "" })}</td>
                    <td>{fmtMs(r.timeToRingMs)}</td>
                    <td>{r.forwardedFrom ?? "—"}</td>
                    <td>{t(`hoptest.outcome.${r.outcome}`)}</td>
                  </tr>
                ))}
                {h?.result?.no_reply && (
                  <tr class={h.result.no_reply.ok ? "" : "mismatch"}>
                    <td>{t("hoptest.no_reply_row")}</td>
                    <td>{h.result.no_reply.expected_member ?? "—"}</td>
                    <td>{h.result.no_reply.rang_member ?? "—"}</td>
                    <td>{fmtMs(h.result.no_reply.time_to_ring_ms ?? null)}</td>
                    <td>{h.result.no_reply.timer_s !== undefined ? `${h.result.no_reply.timer_s} s` : "—"}</td>
                    <td>{h.result.no_reply.ok ? t("hoptest.outcome.ok") : t("hoptest.outcome.no_ring")}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {h?.audit && (
            <p class="hint">{t("hoptest.audit", { when: new Date(h.audit.ts).toLocaleString(), action: h.audit.action })} <code class="ltr">{JSON.stringify(h.audit.detail)}</code></p>
          )}
          <div class="row">
            {terminal && h?.passed && <button type="button" class="primary" disabled={busy} onClick={() => void finish()}>{t("hoptest.finish")}</button>}
            {terminal && !h?.passed && <button type="button" class="primary" disabled={busy} onClick={() => { runner.retry(); void start(new Event("submit")); }}>{t("hoptest.rerun")}</button>}
            {terminal && !h?.passed && !h?.required && <button type="button" disabled={busy} onClick={() => { runner.retry(); void skip(); }}>{t("hoptest.skip")}</button>}
          </div>
        </section>
      )}
    </div>
  );
}
